define((require) => {
    "use strict";

    const _                 = require("underscore");
    const AssociatedModel   = require("backbone.associations");
    const Backbone          = require("backbone");
    const ErrorHandler      = require("shared/services/error/ErrorHandler");
    const ImprovedModel     = require("shared/zbone/ImprovedModel");
    const {Logger}            = require("shared/util/Logger");
    const Permissions       = require("shared/security/Permissions");
    const Preconditions     = require("shared/util/Preconditions");
    const Promise           = require("shared/util/Promise");
    const Syncer            = require("shared/services/sync/Syncer");
    const SyncManager       = require("shared/services/sync/SyncManager");

    // COLLECTIONS

    const _fetch = Backbone.Collection.prototype.fetch;

    let SEQUENCE = 1;

    // Mixin "fetch" for collections so the sync manager is used for consistency and to display spinner when
    // collections are loaded as well. We cannot use the standard "mixin" functionality here as it has not
    // been defined on Backbone types...
    Backbone.Collection.prototype.fetch = function(options) { // We really need a common super collection type like Class!
        var collection = this;

        return SyncManager.sync(
            {
                method:   "fetch",
                immutable: true,
                silent:    options && options.silent,
                fn:        function() {
                    return _fetch.call(collection, options);
                }
            },
            collection
        ).promise();
    };
    // Mixin somewhat meaningful "toString" for collections...
    Backbone.Collection.prototype.toString = function() {
        // Generate unique id for *this* collection for easier debugging...
        if (!this.id) {
            this.id = SEQUENCE++;
        }

        var s = "Collection[id: " + this.id;

        if (this.model.__typeName) {
            s += "; model: " + this.model.__typeName;
        }

        return s + "; size: " + this.length + "]";
    };

    const log = new Logger("ImprovedModel");

    // UPDATE LOGIC
    //
    // Note: defined here, outside ImprovedModel, as it must work on both normal Backbone.Model and ImprovedModels alike!

    // Frontend field used to store the identity for non-persisted identifiable models. We have patched
    // Backbone.Model so no attribute named "cid" can be stored on any model!...
    const TEMP_ID = "cid";
    const UNIQUE_ID = "uuid";

    // Default fields to exclude on clone({duplicate:true}).
    const META_FIELDS_DUPLICATE = ["id", "version", "createdAt", "editedAt", UNIQUE_ID];

    // Default fields to exclude on clone({duplicate:true, duplicateExperienceWithUUID: true}). (PE-1461 for more details)
    const META_FIELDS_DUPLICATE_WITHOUT_UUID = ['id', 'version', 'createdAt', 'editedAt'];

    // Default fields to update for identifiable models on sync that will have performed some update,
    // e.g. "save" or even "push" for experiences, none of them silent! TEMP_ID not included as it will
    // only ever be present when saving to the server and not updated based on the response!...
    const META_FIELDS_UPDATE = ["id", {name: "version", silent: true}, "createdAt", "createdBy", "editedAt", "editedBy"];

    // Test if the supplied model/collection is considered identifiable: can be identified via the "id" property,
    // alternatively via the TEMP_ID property until created. This odd construct is for legacy purposes, so we do not
    // have to mark all our top level collections as identifiables...
    const identifiable = function(o) {
        return o && (typeof o.identifiable === "undefined" || o.identifiable);
    };

    /**
     * @private
     * Returns the (temp) id assigned to any Backbone.Model, need not be an ImprovedModel.
     *
     * @param {Backbone.Model} model The model to find the id for; never null.
     * @return {String} The id; never null.
     */
    const identifier = function(model) {
        return model.get("id") || model[TEMP_ID]; // not an attribute, Backbone internal!
    };

    /**
     * @private
     * Returns the preferences for doing an update of the specified model after sync for the sync method specified
     * by the supplied options. <p>
     *
     * Implementation note: declared as a function like this it must also work for standard Backbone.Model
     * types, not just ImprovedModels. See _update(..) for usage!
     *
     * @param {Backbone.Model} model The Backbone.Model or ImprovedModel to use; never null.
     * @param {*} [options] The options optionally specifying the sync to be performed against the
     *                      server; can be falsey. The sync method tested is specified as {sync:String},
     *                      e.g. "sync:save" or "sync:fetch". If falsey, "update:all" is returned.
     *
     * @return {update:String, attributes: []} The update preferences, where <tt>update</tt> will be either
     *         "all", "no", or "attributes", and the <tt>attributes</tt> array will contain the attributes
     *         to update in case "update:attributes" (can be empty).
     */
    const updateOnSync = function(model, options) {
        // Common cases, the complex logic is only really for updates like "save", or even "push" for
        // experiences. Note that we will in fact always have "options" here when we are fetching via
        // http, as we (probably in the Syncer or is it Backbone ?) somewhere down the line mixes
        // incoming properties with http ones. This also means we cannot use reversed option names
        // like "method"...
        if (!options || !options.sync || options.sync === "fetch") {
            return {update: "all"};
        } else if (options.sync === "delete") {
            return {update: "no"};
        }

        // Specific attributes to update, or perhaps preferences to update as above...? This will work with
        // both Backbone.Model and ImprovedModel types...
        const preferences = _.isFunction(model.updateOnSync) ? model.updateOnSync(options) : model.updateOnSync;

        // No preferences is determined to be no relevant attributes for this model, though its
        // relations may be updated recursively...
        // eslint-disable-next-line eqeqeq
        if (preferences == null || Array.isArray(preferences)) {
            return {update: "attributes", attributes: preferences}; // undefined/null array means empty!
        }

        // Fancy short-cuts for models...
        const type = typeof preferences;
        if (type === "boolean") {
            return {update: preferences ? "all" : "no"};
        } else if (type === "string") {
            return {update: preferences};
        }
        // Assume preferences as-is...
        return preferences;
    };

    /**
     * @private
     * Updates the specified model recursively after a sync of the specified type, e.g. "save". We have to do this
     * always for the entire graph to handle meta data and newly assigned ids to objects even if they
     * have moved while the request was pending. <p>
     *
     * Due to the problems listed in "ImprovedModel.parse", we have to update the *existing* model graph with required
     * data to avoid loosing the existing graph/models.
     *
     * @param {Backbone.Model} root    The Backbone root model to start the update from ("this"); never falsey.
     * @param {*}              json    The json to update <tt>model</tt> with based on the attributes specified
     *                                 to be updated via <tt>model.updateOnSync</tt> preferences; can be falsey,
     *                                 e.g. no server response.
     * @param {*}              options The options used, where <tt>options.sync</tt> specifies the update to perform.
     *                                 Null will result in a full model update.
     */
    const updateModel = function(root, json, options) {
        // Implementation note: we loop here instead of calling this recursively on each model as we have a mix
        // between improved models *and* standard backbone models; the latter will not support this function,
        // but can still declare "updateOnSync". This is especially important here, as "_update" is a new
        // function, with no default implementation like "toJSON"...

        if (!json) {
            return;
        }

        // Pick changed attributes based on json "data" to update "model" in two maps: one for silent,
        // one for loud (triggering relevant change events). Changes to attributes that are relations
        // will be coerced into loud...
        var pick = function(model, data, attributes) {
            var result = {silent: null, loud: null, relations: null},
                relationType = function(name) {
                    var relation,
                        i;

                    if (model.relations) {
                        for (i = 0; i < model.relations.length; i++) {
                            relation = model.relations[i];
                            if (relation.key === name) {
                                return relation.type;
                            }
                        }
                    }
                    return null;
                },
                map = function(name) {
                    var map = result[name];
                    if (!map) {
                        result[name] = map = {};
                    }
                    return map;
                },
                add = function(silent, name, value) {
                    var relation = relationType(name),
                        update = false,
                        oldValue;

                    // If an attribute is in fact a relation, the entire relation will be updated based on the
                    // data, meaning contained models will be updated fully recursively. We have to test for
                    // this here as we must enforce it as loud...
                    if (relation) {
                        map("relations")[name] = relation;
                        update = true;
                        silent = false; // always set entire relationships loudly...
                    }

                    // Do not bother trying to update non-changed values, so by testing this here, we get better
                    // log messages and can perhaps avoid firing up heavy Backbone change logic whenever possible...
                    // eslint-disable-next-line eqeqeq
                    if (value != null) {
                        oldValue = model.get(name);
                        // Skip updating empty arrays...
                        // eslint-disable-next-line eqeqeq
                        update = (Array.isArray(value) && !value.length && Array.isArray(oldValue) && !oldValue.length) ? false : oldValue != value;
                    }

                    if (update) {
                        map(silent ? "silent" : "loud")[name] = value;
                    }
                };

            var attribute,
                i;

            for (i = 0; i < attributes.length; i++) {
                attribute = attributes[i];
                // Handle attribute specified either as a string, e.g. "foo", which will be loud, or as
                // options like {name:String, silent:Boolean}, which may or may not be loud/silent...
                if (typeof attribute === "string") {
                    add(false, attribute, data[attribute]);
                } else {
                    add(attribute.silent, attribute.name, data[attribute.name]);
                }
            }
            return result;
        };

        var silentOptions = {silent: true, triggeredByServerUpdate: true},
            loudOptions   = {triggeredByServerUpdate: true};

        // Pairs existing models in a non-empty Backbone.Collection with the returned json, taking temp ids into
        // account to ensure changes to the collection will still match the correct data...
        var updateCollection = function(collection, data) {
            var map = {},
                model,
                id,
                i;

            // Index the json models from the response via their id *and* via the temp id we assigned to them...
            for (i = 0; i < data.length; i++) { // json array!
                model = data[i]; // json
                id = model.id;
                if (id) { map[id] = model; }
                id = model[TEMP_ID];
                if (id) { map[id] = model; }
            }

            // Now update each existing model, ignoring those where we have no match in the response, i.e. those
            // created on the frontend while the request was in flight...
            collection.each((collectionModel) => {
                var model = map[identifier(collectionModel)]; // may not be an ImprovedModel!
                if (model) {
                    update(collectionModel, model);
                }
            });
        };

        // This will never update the relationship itself, only contained models within such relationships. It will
        // also ensure that models moved around in Backbone.Many relations will still match...
        var internalUpdate = function(model, data) {
            // Will work for any Backbone.Model type, not just our ImprovedModel...
            var preferences = updateOnSync(model, options),
                attributes,
                values;

            // Do we need to update the entire model, or not at all? If so, we can return early...
            if (preferences.update !== "attributes") {
                return preferences;
            }

            // Get attributes then, can still be falsey though, i.e. no attributes to update for
            // "model", but we must still handle relations below...
            attributes = preferences.attributes;

            // If the model is identifiable, let's update the standard meta fields...
            if (identifiable(model)) {
                if (attributes) {
                    attributes = META_FIELDS_UPDATE.concat(attributes);
                } else {
                    attributes = META_FIELDS_UPDATE;
                }
            }

            // Find attributes and update "model"; the values may be the same as the existing ones, of course.
            // Even if we have no attributes, e.g. for non-identifiables types, we still want to traverse the
            // relations below...
            if (attributes) {
                values = pick(model, data, attributes);

                // Silent...
                if (values.silent) {
                    log.info("Updating silently: " + model, model, values.silent);
                    model.set(values.silent, silentOptions);
                }
                // Loud...
                if (values.loud) {
                    log.info("Updating: " + model, model, values.loud);
                    model.set(values.loud, loudOptions);
                }
            }

            // Recurse to ensure we update the graph recursively, *unless* the relation has already
            // been fully updated because it was specified as an attribute to update!
            if (model.relations) {
                var relation,
                    existing,
                    name,
                    value,
                    i;

                for (i = 0; i < model.relations.length; i++) {
                    relation = model.relations[i];
                    name = relation.key;

                    // Skip relation if already fully recursively updated because it was specified
                    // as (an always loud) attribute above. An example of this is ProjectModel.inferredAssets...
                    if (values && values.relations && values.relations[name]) {
                        continue;
                    }

                    existing = model.get(name);
                    value = data[name];

                    // Only update if we have a value; we never remove relationships as we do not know if
                    // they use a default or what not. It is fine to test on "if (value) { .. }" as we
                    // know "value" is a relationship, not a primitive falsey type like Boolean or Number...
                    if (value) {
                        if (existing) {
                            // For many relationships, ensure that we update correctly, even if the contained
                            // models have been shuffled around since we issued the server sync...
                            if (relation.type === Backbone.Many) {
                                if (existing.length) {
                                    updateCollection(existing, value);
                                } else if (value.length) {
                                    model.set(name, value, loudOptions);
                                }

                            // Single model (Backbone.One or Backbone.Self), so update in-place...
                            } else {
                                update(existing, value);
                            }

                        // Create relationship then, loudly...
                        } else {
                            model.set(name, value, loudOptions);
                        }
                    }
                }
            }
        };

        // Updates the specified model recursively based on the supplied data, stopping if the model is now
        // fully updated...
        var update = function(model, data) {
            var preferences = internalUpdate(model, data);
            if (preferences && preferences.update === "all") {
                model.set(data, loudOptions);
            }
        };

        // Go!
        update(root, json);
    };

    //

    /**
     * Editor "sync mixin" functionality for <tt>ImprovedModel</tt>.
     *
     * @class ImprovedModel
     */
    return ImprovedModel.mixin("ImprovedModelSyncMixin", {

        /**
         * @private @type {Boolean}
         * Flag set to true if <tt>destroy()</tt> has successfully be invoked on this model, i.e. destroyed and
         * all future sync requests should be ignored silently.
         */
        _destroyed: false,

        /**
         * @type {[]|*}
         *
         * Preferences on how to update *this* model on any mutable server sync operation, like "save",
         * or even "push" for Experiences. Any relation will be traversed automatically and updated
         * recursively. <p>
         *
         * For versioned types, the attributes to update can only be those set by the Backend; all other
         * changes done externally will cause an optimistic locking error and can thus be considered
         * immutable on the frontend during sync. <p>
         *
         * The format on how to update can be either: <ul>
         *
         * <li> Array of attributes to update, either like {name:String, silent:Boolean} or just by a String
         *      name meaning {name:String, silent:false}. <p>
         *
         *      For any identifiable type, the "id", optional "version", "createdAt", "createdBy", "editedAt",
         *      "editedBy" attributes will be updated, all loudly. That is, do not specify these fields,
         *      already handled (added to the total list of attributes) <b>unless</b> a given identifiable
         *      type specifies a  preference like below to update entire the model. <p>
         *
         *      If a relation is specified as a attribute, it will be fully updated recursively. Also note
         *      that any model stored in a relation will be updated based on its preferences like here! <p>
         *
         *      The outcome of specifying a computed property is undefined. <p>
         *
         *      Only attributes having a non-null and different value will cause an update of this model.
         *      All such silent and loud attribute updates, respectively, will be updated in one go, thus
         *      at most two Backbone <tt>set(.., ..)</tt> calls, one for silent attributes, one for loud ones.
         *      During <tt>set</tt>, the options passed along will specify at least
         *      {triggeredByServerUpdate: true, [silent: true,] ..}. <p>
         *
         *
         * <li> {update:String}, where "update" can take the values "all" or "no". "all" means the entire model
         *      is recursively updated based on the response json, "no" means no update is performed at all,
         *      which is not allowed for identifiable types. A full update is always loud, never silent. <p>
         *
         *
         * <li> {update:"attributes", attributes:[]}, where the array of attributes is defined as the first
         *      bullet above. The attributes has the exact same semantics as described in the first
         *      bullet above. <p>
         *
         *
         * <li> A function like <tt>fn(options)</tt> that returns either of the above, where <tt>options.sync</tt>
         *      is a String specifying the sync in progress, e.g. "save" or even "push" for Experiences. Normally
         *      only needed in case different sync requests returns quite different response data, like
         *      Experience.push that expresses business logic regarding push state, purge time, etc.
         *
         * </ul>
         *
         * Note: for now, this is not used by "delete" and "fetch", but the logic supports it already! The rationale
         * is simply that we always update all on "fetch" and nothing on "delete", though we could express this via
         * these very semantics.
         */
        updateOnSync: null,

        /**
         * @type {Array}
         *
         * Array of transient property names that will not be persisted during sync.
         */
        transient: [],

        /**
         * Mixin constructor.
         */
        constructor() {
            // Embedded models cannot be accessed individually, so never mind about the sync state...
            if (this.embedded) {
                return;
            }
            // Initialise sync state for each model, so bulk fetched models will have a default state upon creation
            // so we do not have to check for presence of "model.syncState" everywhere...
            Syncer.State.init(this, {
                unique: ["id", TEMP_ID, UNIQUE_ID],
                // To allow sub-types to override...
                permissions: {
                    save:    Permissions.Privileges.CREATE,
                    destroy: Permissions.Privileges.CREATE
                },
                alias: {
                    destroy: "delete"
                }
            });
        },

        /**
         * Returns the identifier for this model.
         *
         * @return {String} The identifier; never null.
         */
        identifier() {
            return identifier(this);
        },

        isNew() {
            // Models known to be identifiable embedded entities are not persistent, not those explicitly
            // tagged as immutable!...
            return this.embedded || this.syncState.immutable || AssociatedModel.prototype.isNew.call(this);
        },

        /**
         * Overrides the standard Backbone.Model <tt>parse</tt> method to handle updates of specific properties
         * instead of the entire model recursively.
         *
         * @param {*} response  The response to for this model, including all embedded data; never null.
         * @param {*} [options] The options, if any; will contain {sync:String}, e.g. {sync:"save"} on
         *                      backend save.
         *
         * @return {*} The (recursive) json for this model; never falsey.
         */
        parse(response, options) {
            // We know we do not have to do any parsing from the super model as no parsing is done by it. And
            // since we have overridden this exact method to do our bidding, this is the parse function called
            // and we know the response is valid for our use...

            // On "sync:method" for updates, e.g. "sync:save", we only return the id as the parse logic is
            // impossible (!) to hook into while maintaining model identity without a major rewrite of
            // Backbone.Model/Collection *and* support a partial update.
            if (updateOnSync(this, options).update === "all") {
                return response;
            }

            // Note that "response" is a top-level response at this point. Since we only return the id, we will
            // *never* recurse into relations reachable from this type, and this parse function will *not* be
            // called recursively. We do this because Backbone does not handle partial updates well on individual
            // nested attributes in an existing graph; Backbones matches on id attribute alone, not our temp
            // ids required to handle our use case, and even on match, it creates new relations as well, where we
            // want to update existing ones. It also mixes in default (and computed) attributes on match as well,
            // which is incompatible with partial updates as well. Because of this, we have to traverse the
            // relations after this update in our "doSave" function to perform the required updates then, taking
            // both id and our temp ids into account to ensure moved models in collections will still be matched...
            return {
                id: response.id
            };
        },

        /**
         * Overrides the standard backbone.model <tt>toJSON</tt> method to handle transient and computed properties
         * recursively, including support for specific properties to clear on clone.
         *
         * @param {*} [options] The options; will contain {duplicate: true} on clone used to duplicate models to
         *                      be persisted as new entities, {sync:String} on backend sync to update this model,
         *                      e.g. "save", "delete", or even other types like "push" for experiences.
         *
         * @return {*} The (recursive, possibly cloned) json for this model; never falsey.
         */
        toJSON(options, exclude) {
            options = options || {};
            exclude = exclude || [];

            // The AssociatedModel ensures relationships are traversed, i.e. this function will be called recursively
            // for relationships where relevant. It will not, however, clone arrays and objects not mapped as
            // relationships, which is a serious problem (shallow copy)...
            var json = AssociatedModel.prototype.toJSON.call(this, options),
                clear = _.isFunction(this.clearOnClone) && this.clearOnClone(options),
                transform,
                ignore = {},
                attribute,
                value,
                transient,
                tempId,
                i;

            if (Array.isArray(clear)) {
                exclude = exclude.concat(clear);
            }

            // Exclude common specified meta fields on duplication - we do include computed ones (should we?)...
            // When duplicateExperienceWithUUID is true, include the 'uuid' field. (PE-1461 for more details)
            if (options.duplicate) {
                const metaFieldsDuplicate = options.duplicateExperienceWithUUID ? META_FIELDS_DUPLICATE_WITHOUT_UUID : META_FIELDS_DUPLICATE;
                exclude = exclude.concat(metaFieldsDuplicate);

            // Exclude computed properties on saving to backend (say, instead of cloning to some frontend template),
            // and ensure any new identifiable model has a "tempId" so we can identify it upon server
            // return in case on intermediate structural changes. However, each computed property can specify it
            // as being not transient for properties that has been resolved to a Model instance based on for
            // example a reference...
            } else if (options.sync) { // we only use "sync" for updates with regards to marshalling *to* json!
                exclude = exclude.concat(this.transient); // always exclude transient properties...

                if (this.computed) {
                    for (attribute in this.computed) {
                        transient = this.computed[attribute].transient;
                        if (typeof transient === "undefined" || transient) {
                            exclude.push(attribute);
                        }
                    }
                }

                // Assign a temp id to any currently non-identifiable model sent to the server as part of a
                // Backbone.Many relationship, so we can find it afterwards even if moved in the collection
                // before we get a server response. A non-identifiable model is a new instance of any identifiable
                // model type as it has no id yet, or any non-identifiable type altogether (where the backend
                // never assigns an id to it). We *only* do this when individual attributes are updated, not if the
                // entire model is updated since no bookkeeping will be needed in that case - everything will be
                // overridden in that case based on the response. We test early on "id" since we expect that to be
                // set for the most cases, ensuring we can skip additional checks faster...
                if (!this.get("id") && options.relation && options.relation.type === Backbone.Many) {
                    // We manipulate the json, not the models, as the temp id will be excluded there...
                    tempId = this[TEMP_ID];
                    // Note: on full update on models, the TEMP_ID may be stored as an attribute based on the response!
                    // It seems excessive to fix this edge case, so we ignore it for now as it should not really
                    // matter with respect to correctness...
                    json[TEMP_ID] = tempId;
                    log.verbose("Assigned temp id: '" + tempId + "' -> " + this, this);
                }

                // Attributes to transform before sync, only in the json, not the actual models. E.g. collapsing a model
                // into a reference (again)...
                if (_.isFunction(this.transformBeforeSync)) {
                    transform = this.transformBeforeSync(options);
                }
            }

            // No need to deep copy attributes to exclude or already handled as relationships (most cases)...
            for (i = 0; i < exclude.length; i++) {
                ignore[exclude[i]] = 1;
            }

            if (this.relations) {
                for (i = 0; i < this.relations.length; i++) {
                    ignore[this.relations[i].key] = 2;
                }
            }

            // Make sure we make (semi-) deep copies of references that are objects or arrays *not* mapped as
            // relationships, as those are already handled. Otherwise the copy will potential shared state, bad for
            // so many reasons...
            for (attribute in this.attributes) {
                if (!ignore[attribute]) {
                    value = this.attributes[attribute];
                    if (Array.isArray(value) || _.isObject(value)) {
                        // Still not nested, we assume no unmapped nested relationships like this! We might want to
                        // revisit this if nested mappings like this becomes used...
                        json[attribute] = _.clone(value);
                    }
                }
            }

            // Only on sync...
            if (transform) {
                for (i = 0; i < transform.length; i++) {
                    attribute = transform[i];
                    if (!ignore[attribute.name]) {
                        // Note: because of the way Backbone.Model/Relation works, we may or may not supply
                        // a model or a json structure to the transform handler! Be sure it can handle both cases
                        // if applicable (depends on how the model type in question defines it relations)...
                        json[attribute.name] = attribute.transform(json[attribute.name]);
                    }
                }
            }

            // Implementation notes: do not use _.omit as it copies the json - we already have the json we need, so
            // simply modify it instead. Also, do not use forEach to loop, this is critical functionality and should
            // be as fast as possible!
            for (i = 0; i < exclude.length; i++) {
                delete json[exclude[i]];
            }

            return json;
        },

        /**
         * Clones this improved model recursively, with support for clearing fields such as internal ids (so the
         * clone can be persisted as a new object). <p>
         *
         * The options will be supplied to the <tt>clearOnClone(options)</tt> handler defined by this model, if
         * any, which can return an array of additional fields to clear in the returned clone. <p>
         *
         * On <tt>{duplicate: true}</tt>, the fields "id", "version", "tempId", "createdAt", and "editedAt" are cleared
         * recursively for any referenced model (so they do not need to be specified in the hook). In addition,
         * the "copyOf" property is set to the internal id of *this* model (defaulting to "copy") in the duplicate,
         * so we can trace the duplication.
         *
         * @param   {*} [options] Clone options, e.g. none to get an exact clone except transient properties;
         *                        <tt>{duplicate: true}</tt> to get a clone to be persisted (getting new ids);
         *                        <tt>{sync: String}</tt> will ignore any computed properties; etc. Can
         *                        be falsey, i.e. returns an exact clone except transient properties.
         * @returns {ImprovedModel} A deep (recursively cleared) clone of this model; never null.
         */
        clone(options) {
            // Make sure same options instance passed to both toJSON and hooks...
            options = options || {};
            // Cannot call super since it calls toJSON with no options...
            var clone = this.toJSON(options, [UNIQUE_ID]);

            // On duplicate, tag clone with origin id, if any, so we can trace the origins. We always set a value, both
            // to ensure we do not pass along an origin from another copy, but also to ensure that we can always trace
            // copies...
            if (options.duplicate) {
                clone.copyOf = identifier(this) || "copy";
            }

            return new this.constructor(clone, options); // pass along options as they will be passed to fx initialize
        },

        /**
         * Overrides standard Backbone.Model.set to ensure that a meta data describable model in its meta data
         * form is immutable; trying to change it is a programmer fatal error except from server responses!
         */
        set(key, val, options) {
            // Allow mutation from server responses from sync requests. We do not raise exceptions here, since we
            // want the SyncManager to raise a validation error on dequeue time so the user will be notified. By using
            // preconditions, we will still track this scenario as well...
            var valid = !_.isString(key)      || // for now, we only support a single key check... :/
                        this.get(key) === val || // has value not changed?
                       !this.has("immutable") || // is this type not immutable?
                       !this.get("immutable") || // is this instance not currently immutable?
                // eslint-disable-next-line no-mixed-operators
                       (options && options.computedProperty || this.transient.indexOf(key) !== -1) || // ignore computed/transient props
                       (this.embedded || !Syncer.State.complete(this)); // allow changes while syncing...
            Preconditions.checkState(valid, "Change to immutable model: '" + key + "' = '" + val + "' != '" + this.get(key) + "' -> " + this); // will be logged by frontend error logger!

            return AssociatedModel.prototype.set.call(this, key, val, options);
        },

        /**
         * Forces a recomputation of any computed properties that depends on the specified array of property
         * names. This might be useful following a silent update of specific model properties, since computed
         * properties are only computed during regular model changes, i.e. "non silent".
         *
         * @param  {String|String[]} dependencies A single property or array of property names.
         * @param  {Boolean}         silent       Optional, whether the update of the computed property should
         *                                        be <i>silent</i> or not.
         */
        invalidateComputedProperties(dependencies, silent) {
            if (this.computed) {
                const deps = Array.isArray(dependencies) ? dependencies : [dependencies];

                _.each(this.computed, (config, propertyName) => {
                    config.silent = silent;

                    if (config.deps) {
                        _.each(config.deps, (dependency) => {
                            if (deps.indexOf(dependency) !== -1) {
                                this._computeProperty(propertyName, config);
                            }
                        });
                    }
                });
            }
        },

        /**
         * @private
         * Sync method callback to determine if a sync request for this model is cancelable or not for the
         * specified sync method.
         *
         * @return {Boolean} True if cancelable, false otherwise.
         * @see    #_syncOptions
         */
        _cancelable() {
            return !this.has("version");
        },

        /**
         * Sync method callback to handle the supplied error for the current sync method when a sync fails. <p>
         *
         * This will tag <tt>error.model</tt> with a reference to this (failing) model and resolve the error
         * message accordingly based on the model data, e.g. distinguishedName for ${name} placeholder in the
         * error message.
         *
         * @param  {*} error The incoming http error as {message:String, status:*, ..}; never null.
         *
         * @return {String} The string "alert"; never null.
         *
         * @see    #_syncOptions
         * @see    ErrorHandler#fail
         */
        _handleError(error) {
            var syncState = this.syncState,
                typeName  = this.__typeName || "resource",
                method    = syncState.current.method;

            // Make sure we use registered alias, e.g. "share" instead of "save" when sharing...
            method = syncState.alias[method] || method;

            // Make sure we only use DUPLICATE for experiences and not on destroy (delete)...
            if (error.action && (method === "destroy" || typeName !== "Experience")) {
                error.action = error.action.replace(ErrorHandler.Actions.DUPLICATE,  "");
            }

            // Tag the error with this model as the failing model, which will then be used for defaults during
            // value injection before the error is presented...
            error.model = this;

            // This will force the error to be logged and it to be tracked. Note that calling the "url()" function
            // can raise the very same error we came here for, e.g. trying to sync without an url, so we must take
            // that into account...
            var url = "";

            if (this.url) {
                if (_.isFunction(this.url)) {
                    try {
                        this.url();
                    } catch (e) {
                        // In case of urlError raised by Backbone sync...
                    }
                } else {
                    url = this.url;
                }

                if (url) {
                    url = " -> " + url;
                }
            }

            Preconditions.fail(method + " failed with status " + error.status + " for the " + typeName + ": " + this + url);

            return "alert";
        },

        /**
         * @protected
         * Augments a copy of <tt>options</tt> with a standard set of sync options for the specified sync method
         * to be executed.
         *
         * @param  {String|*} options The (public) sync method as a string, e.g. "save", or options to use, merged
         *                            with a default set of applicable options. Example:
         *                            {method: "save", silent: true, delay: 2000}. Will not be changed by this method.
         * @return {*} A new set of sync options; never falsey.
         */
        _syncOptions(options) {
            if (typeof options === "string") {
                options = {method: options};
            }

            var immutable = options.method.indexOf("fetch") === 0,
                update;

            // All callbacks invoked with target scope. Note that we make a copy of "options" here!
            options = _.extend({
                sync:         options.method, // for parse/toJSON callbacks, only used for responses!
                immutable:    immutable, // to avoid fetching again while still an active one pending
                privilege:    this.syncState.permissions[options.method], // can be undefined!
                cancelable:   function() { return immutable || this._cancelable(); },
                errorHandler: this._handleError,
                validate:     function() {
                    if (this._destroyed) {
                        return "destroyed";

                    // Never allow a mutable request for any model currently tagged as immutable...
                    } else if (update && this.has("immutable") && this.get("immutable")) {
                        return "immutable";
                    }
                    return null;
                }
            }, options);

            update = !options.immutable && options.method !== "destroy"; // E.g. "save"...

            // If no "fn" defined, private method, e.g. "_save" will be executed...
            if (!options.fn && options.method.charAt(0) !== "_") {
                options.method = "_" + options.method;
            }

            return options;
        },

        /**
         * @private
         * Calls the sync method from <tt>AssociatedModel</tt> prototype with the supplied options.
         *
         * @param {String}  method   The sync method to execute, e.g. "save", "fetch", etc.
         * @param {*}      [varArgs] The sync arguments, if any.
         * @return {Promise|Boolean} The promise from the <tt>AssociatedModel</tt> sync method, or false
         *                           if no server sync is deemed necessary by the <tt>AssociatedModel</tt>.
         */
        _superSync(...args) {
            const method = args.shift();
            return AssociatedModel.prototype[method].apply(this, args);
        },

        /**
         * Overrides the standard backbone.model save method. The default save method updates the model when the server
         * response is received, which is undesired behavior for the model. This implementation (in conjunction) with
         * the override of backbone.model.parse, makes sure that the models aren't updated when saving. <p>
         *
         * We never use <tt>key</tt> and <tt>val</tt> directly, but we must preserve the signature to ensure that
         * us being called by Backbone will not change the semantics.
         */
        save(key, val, options) {
            return this._doSave(key, val, options);
        },

        /**
         * Same as #save, but with a pending delay of 6s. This ensures that a "sync:save:pending" event will
         * be triggered for this model, unless already syncing.
         *
         * @param {Number} [delay] The pending delay in ms, defaulting to 6s.
         */
        saveLater(delay, key, val, options) {
            return this._doSave(key, val, options, delay || 6000); // 6s delay by default
        },

        _doSave(key, val, options, delay) {
            if (this.embedded) {
                return this._superSync("save", key, val, options);
            }
            var opts = {method: "save", delay: delay, silent: options && options.silent};
            return SyncManager.sync(
                this._syncOptions(opts),
                this,
                key, val, options
            ).promise();
        },

        /**
         * @private
         * Performs the actual remote save of this model, called via the sync manager. <p>
         *
         * We never explicitly use neither "key" or "val", but Backbone will pass along "key" = null explicitly
         * in certain cases (and thus "options" as the second argument), so we must respect that and act accordingly!
         * The general case is, however, only zero (default options) or a single argument, "options".
         */
        _save(key, val, options) {
            const generateOptions = (o) => {
                const saveOptions = {sync: "save"};
                return o ? _.extend(o, saveOptions) : saveOptions;
            };

            // "options" can be supplied as the second or third argument, and "key" can be a string in which case
            // "val" is never options, or "key" can be an object in which case "val" is "options"...
            if (!key || typeof key === "object") {
                val = generateOptions(val);
            } else {
                options = generateOptions(options);
            }

            // The marshalling to json will assign temp ids to new identifiables about to be created, which we can
            // already call now as the super sync functionality will have performed the marshalling already...
            return SyncManager.cancelable(this, this._superSync("save", key, val, options)).done((json) => {
                this._update(json, val || options); // executed *before* "sync:state:synced" fires!
            });
        },

        /**
         * Updates this model recursively based on the supplied json representation, issuing all standard
         * "sync:state" events based on <tt>options.method</tt> for this model. Since the json representation
         * to update this model with is already available, this will be executed synchronously, issuing the
         * relevant "sync:state" events in rapid synchronous order. <p>
         *
         * This is typically done when a sync of some other model causes this model to be fetched/updated as auxiliary
         * data. No server round trip is done, so the "sync:state" events will come rapidly. <p>
         *
         * There is normally no need to call this method.
         *
         * @param {*} json    The json to update this model with based on the attributes specified to be updated
         *                    via <tt>model.updateOnSync</tt> preferences. Must defined an <tt>id</tt> property that
         *                    matches the id of this model.
         * @param {*} options The options used, where <tt>options.sync</tt> specifies the update to perform. Null
         *                    will result in a full model update.
         * @return {Promise} A promise that resolves once the update is done (which will resolve "now"); never falsey.
         * @throws Error If <tt>json</tt> does not defined an <tt>id</tt> property that is equal to the id of this
         *               model.
         */
        update(json, options) {
            var self = this,
                id = self.get("id");

            Preconditions.checkState(id === json.id, new Error("Id mismatch: '" + id + "' != '" + json.id + "'"));

            options = this._syncOptions({
                method:    options.sync || options.method, // prefer "options.sync" as that will never be a private method...
                silent:    options.silent,
                immutable: options.immutable,
                privilege: options.privilege,
                promise:   options.promise,
                fn:        function() { self._update(json, options); } // executed *before* ("now") "sync:state:synced" fires!
            });

            return SyncManager.sync(options, self).promise();
        },

        /**
         * @private
         * Updates this model recursively after a sync of the specified type, e.g. "save". We have to do this
         * always for the entire graph to handle meta data and newly assigned ids to objects even if they
         * have moved while the request was pending. <p>
         *
         * Due to the problems listed in "parse", we have to update the *existing* model graph with required
         * data to avoid loosing the existing graph/models. <p>
         *
         * There is normally no need to call this method; the update is handled automatically after a sync, but
         * it may be needed in certain cases where json updates are returned as a side effect of another operation,
         * like a push with assets for a group. <p>
         *
         * If <tt>json</tt> is an array, e.g. auxiliary models, this model is expected to have an id already. If not,
         * the correct json model to update based on cannot be found; we only support temp ids for relations
         *
         * @param {*|[]} json The json to update this model with based on the attributes specified to be updated
         *                    via <tt>model.updateOnSync</tt> preferences. It can be a single json object, i.e.
         *                    for this model, or an array of (auxiliary) models, also returned by the api, for
         *                    example because they have been updated (like a version bump). If so, the array
         *                    is searched to find the matching id, using that array entry for update if any. The
         *                    remaining auxiliary objects will be stored under <tt>options.auxiliary</tt> if non-empty.
         *                    If the id of this model cannot be found, no update of this model is performed.
         * @param {*} options The options used, where <tt>options.sync</tt> specifies the update to perform. Null
         *                    will result in a full model update.
         */
        _update(json, options) {
            if (Array.isArray(json)) {
                var data,
                    match = false,
                    i = json.length;

                while (i--) {
                    data = json[i];
                    if (data.id === this.get("id")) { // TODO: eventually support TEMP_ID, but not so simple. Not relevant now, anyways...
                        match = true;
                        break;
                    }
                }

                // Store auxiliary array if it contains other models beside this target...
                if (options && ((match && json.length > 1) || json.length)) {
                    options.auxiliary = json;
                }

                // On match, tag incoming array with target index; we cannot remove the target as other "done"
                // handlers may need to use the data somehow...
                if (match) {
                    json.target = i;
                    json = data;
                } else {
                    json.target = -1;
                    json = null;
                }
            }

            updateModel(this, json, options); // handles falsey (null) json!
        },

        /**
         * Overrides the standard backbone.model destroy method. The default destroy method does not send any
         * model data (json) with the DELETE request, but this is required here to ensure we do not delete
         * non-current versions for versioned entities.
         *
         * @param {Object} [options] The options object, optional.
         * @returns {Promise} The destroy promise; never null.
         */
        destroy(options) {
            if (this.embedded) {
                return this._superSync("destroy", options);
            }
            return SyncManager.sync(
                this._syncOptions({method: "destroy", silent: options && options.silent}),
                this,
                options
            ).promise();
        },

        /**
         * @private
         * Performs the actual remote destroy of this model, called via the sync manager.
         */
        _destroy(options) {
            // Mark this model as destroyed henceforth...
            this._destroyed = true;

            options = _.extend({}, options);

            // Marshal this model to json for the delete; these are the default settings set by Backbone.sync
            // for sync methods expecting the model to be marshalled...
            options.contentType = "application/json";
            options.data = JSON.stringify(this.toJSON({sync: "destroy"}));

            const syncResult = this._superSync("destroy", options);

            // We won't get a Promise if the model has not been synced. See `_superSync`.
            if (Promise.isPromise(syncResult)) {
                // Call Backbone.delete and get the promise. However, if "this.isNew()" is true, false will be
                // returned, since Backbone will not call the backend at all then. This is already handled
                // by the Sync Manager, and the returned promise will just resolved to this model if so...
                return SyncManager.cancelable(this, syncResult.fail(() => {
                    this._destroyed = false; // or a "fetch()" can never succeed again, e.g. for reload
                }));
            }

            return syncResult;
        },

        /**
         * Overrides fetch to use the URL from the "fetchNewUrl" property when fetching a new model, if it is defined.
         *
         * <p>This is useful if you want to load a model initially from a different path than would be used to create
         * or save the model. For example, we look up the settings models as subresources of the company, but once we
         * have the model, we can store and fetch it directly using its ID.</p>
         *
         * @param {Object} [options] The options object, optional.
         * @returns {Promise} The fetch promise; never null.
         */
        fetch(options) {
            if (this.embedded) {
                return this._superSync("fetch", options);
            }
            return SyncManager.sync(
                this._syncOptions({method: "fetch", silent: options && options.silent}),
                this,
                options
            ).promise();
        },

        /**
         * @private
         * Performs the actual remote fetch of this model, called via the sync manager.
         */
        _fetch(options) {
            var parsedOptions = options;

            if (this.isNew()) {
                var url = _.result(this, "fetchNewUrl");

                if (url) {
                    parsedOptions = options ? _.clone(options) : {};

                    if (!parsedOptions.url) {
                        parsedOptions.url = url;
                    }
                }
            }

            // Fetch...
            return SyncManager.cancelable(this, this._superSync("fetch", parsedOptions));
        },

        /**
         * @protected
         * Executes a http sync request for this model, updating it according if a response is returned.
         *
         * @param {String|*} options The (logical) sync method as a string, e.g. "push", or a set of
         *                           sync options to be augmented with applicable defaults for this
         *                           model; cannot be falsey. Will not be modified by this function.
         * @param {*}        request The http options, must include "url", optional "type" (default POST),
         *                           optional "data" properties. "data" will default to the JSON represention
         *                           of this model at sync time.
         *
         * @return {{promise:Promise, options:*}} An object where the <tt>promise</tt> property stores the promise used
         *                                        for execution, and <tt>options</tt> the actual used options to do
         *                                        the syncing; never falsey.
         */
        _execute(options, request) {
            options = this._syncOptions(options);

            var self = this;

            // This is a neat short-hand for issuing a http(s) ajax request...
            options.fn = _.extend({
                type:        "POST",
                dataType:    "json",
                contentType: "application/json; charset=utf-8",

                // Bypass Backbone.Model "parse" logic, which is fine since we have the data to potentially
                // update the graph by...
                success(json) {
                    self._update(json, options); // executed *before* "sync:state:synced" fires!
                }
            }, request);
            return {promise: SyncManager.sync(options, self).promise(), options: options};
        }

    }, {

        /** -----------------------------------
         *  Static mixin methods
         *  ---------------------------------- */

        // TODO: all fetch on cache-misses should update the global cache!
        // TODO: nether of these works for embedded types, how can we avoid exposing them statically then?

        /**
         * Find the first model with the same attributes as the query argument.
         *
         * @param   {Object} query    The query to find the (first) model for; cannot be falsey.
         * @param   {*}     [options] Options to use for the fetch, like {silent: true}; can be falsey.
         *
         * @return {Promise} A promise for the model; never falsey.
         */
        findByQuery(query, options) {
            var Type = this,
                constructorOptions;

            if (options && options.constructorOptions) {
                constructorOptions = options.constructorOptions;
                delete options.constructorOptions;
            }

            // Queue handles correct result (model), so we just return the promise from the "fetch()" as-is... :)
            return new Type(query, constructorOptions).fetch(options);
        },

        /**
         * Find the model with the specified id, taking advantage of the global cache if possible.
         *
         * @param   {String} id       The model id; cannot be falsey.
         * @param   {*}     [options] Options to use if a fetch is required, like {silent: true}; can be falsey.
         *
         * @return {Promise} A promise for the model; never falsey.
         */
        findById(id, options) {
            // Query backend...
            return this.findByQuery({id: id}, options);
        },

        /**
         * Finds a model matching the given attributes, otherwise creates a new one and saves it. <p>
         *
         * A model is considered a match if it has an id matching the id property of the attributes.
         *
         * @param   {Object} attributes The model attributes.
         * @returns {Promise} A promise for the found (and perhaps created) model; never falsey.
         */
        findOrCreate(attributes) {
            var Type = this,
                constructorOptions = attributes.constructorOptions,
                model;

            delete attributes.constructorOptions;

            // Does not exist via id, so check if we can find it via the api, otherwise create...
            return Promise.deferred((deferred) => {
                model = new Type(attributes, constructorOptions);
                // Since queued, we might get a different instance back ("result"), which is the one updated and
                // which must be returned...
                model.fetch().then((result) => {
                    deferred.resolve(result);
                    // No such model, so we create and return the result...
                }, () => {
                    model.save().then((result) => {
                        deferred.resolve(result); // again, use returned model result
                    }, deferred.reject);
                });
            });
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/zbone/ImprovedModelSyncMixin.js